import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import {
  PlmPartner,
  EnumPlmPartnerPartnerType,
  useCreatePlmPartnerMutation,
  useUpdatePlmPartnerMutation,
} from '../../../../types/generated-types';
import { useNavigate } from 'react-router-dom';
import BorderedSection from '../../shared/borderedSection';
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
} from 'libphonenumber-js';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  partnerType: Yup.mixed<EnumPlmPartnerPartnerType>()
    .oneOf(Object.values(EnumPlmPartnerPartnerType))
    .required('Partner type is required'),
  contact: Yup.object().shape({
    name: Yup.string().required('Contact name is required'),
    email: Yup.string()
      .required('Email is required')
      .email('Invalid email format'),
    phone: Yup.string()
      .required('Phone is required')
      .test('valid-phone', 'Invalid phone number', (value) => {
        if (!value) return false;
        return isValidPhoneNumber(value) || false;
      }),
  }),
});

interface FormValues {
  name: string;
  description: string;
  partnerType: EnumPlmPartnerPartnerType;
  contact: {
    name: string;
    email: string;
    phone: string;
  };
}

const calculatePlmPartnerFormValues = (
  plmPartner?: Partial<PlmPartner>,
): FormValues => ({
  name: plmPartner?.name || '',
  description: plmPartner?.description || '',
  partnerType:
    (plmPartner?.partnerType as EnumPlmPartnerPartnerType | undefined) ||
    EnumPlmPartnerPartnerType.Utility,
  contact: {
    name: plmPartner?.contact?.name || '',
    email: plmPartner?.contact?.email || '',
    phone: plmPartner?.contact?.phone || '',
  },
});

interface PlmPartnerDetailFormProps {
  plmPartner?: Partial<PlmPartner>;
  loading?: boolean;
  error?: Error;
}

export const PlmPartnerDetailForm: React.FC<PlmPartnerDetailFormProps> = ({
  plmPartner,
  loading,
  error,
}) => {
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const [createPlmPartner] = useCreatePlmPartnerMutation({
    refetchQueries: ['PlmPartners', 'PlmPartnerDetailEdit'],
  });
  const [updatePlmPartner] = useUpdatePlmPartnerMutation({
    refetchQueries: ['PlmPartners', 'PlmPartnerDetailEdit'],
  });

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = parsePhoneNumberFromString(value);
    return phoneNumber ? phoneNumber.formatInternational() : value;
  };

  const initialValues = calculatePlmPartnerFormValues(plmPartner);

  const onSubmit = async (values: FormValues) => {
    try {
      if (plmPartner?._id) {
        if (!can('update', { __typename: 'PlmPartner' })) {
          Notifier.error('You do not have permission to update PLM partners');
          return;
        }

        await updatePlmPartner({
          variables: {
            id: plmPartner._id,
            plmPartner: {
              ...values,
            },
          },
        });

        Notifier.success('PLM partner updated successfully');
      } else {
        if (!can('create', { __typename: 'PlmPartner' })) {
          Notifier.error('You do not have permission to create PLM partners');
          return;
        }

        const result = await createPlmPartner({
          variables: {
            plmPartner: values,
          },
        });

        if (result.data?.createPlmPartner?._id) {
          Notifier.success('PLM partner created successfully');
          navigate(`/plmPartners`);
        } else {
          Notifier.error('PLM partner creation failed');
        }
      }
    } catch (error) {
      Notifier.error('Error saving PLM partner');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Typography variant="h4" color="secondary">
          Preparing your data..
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ padding: '16px' }}
      >
        <Typography color="error">
          Error loading PLM partner: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        handleChange,
        handleBlur,
        values,
        isSubmitting,
        setFieldValue,
      }) => (
        <Form style={{ padding: '10px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BorderedSection title="PLM Partner Information">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      multiline
                      rows={4}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      error={touched.partnerType && Boolean(errors.partnerType)}
                    >
                      <InputLabel id="plm-partner-type-label">
                        Partner type
                      </InputLabel>
                      <Select
                        labelId="plm-partner-type-label"
                        name="partnerType"
                        label="Partner type"
                        value={values.partnerType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <MenuItem value={EnumPlmPartnerPartnerType.Utility}>
                          Utility
                        </MenuItem>
                        <MenuItem value={EnumPlmPartnerPartnerType.Aggregator}>
                          Aggregator
                        </MenuItem>
                      </Select>
                      {touched.partnerType && errors.partnerType ? (
                        <FormHelperText error>
                          {errors.partnerType as string}
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                  </Grid>
                </Grid>
              </BorderedSection>
            </Grid>

            <Grid item xs={12}>
              <BorderedSection title="Contact Information">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="contact.name"
                      label="Contact Name"
                      value={values.contact.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.contact?.name && Boolean(errors.contact?.name)
                      }
                      helperText={touched.contact?.name && errors.contact?.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="contact.email"
                      label="Email"
                      value={values.contact.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.contact?.email && Boolean(errors.contact?.email)
                      }
                      helperText={
                        touched.contact?.email && errors.contact?.email
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="contact.phone"
                      label="Phone"
                      placeholder="+1 (123) 456-7890"
                      value={values.contact.phone}
                      onChange={async (e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        await setFieldValue('contact.phone', formatted);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched.contact?.phone && Boolean(errors.contact?.phone)
                      }
                      helperText={
                        touched.contact?.phone && errors.contact?.phone
                      }
                    />
                  </Grid>
                </Grid>
              </BorderedSection>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  sx={{
                    backgroundColor: '#36AFA5',
                    '&:hover': { backgroundColor: '#2a7f7c' },
                  }}
                  disabled={isSubmitting}
                >
                  {plmPartner?._id ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
